import { batchActions } from 'redux-batched-actions';
import { call, put, select } from 'redux-saga/effects';
import uuid from 'uuid';
import { LayoutType } from 'const';
import { dragHotspotPosition } from 'containers/App/selectors';
import { updateArtboard } from 'containers/Artboards/actions';
import { activeArtboard } from 'containers/Artboards/selectors';
import { addRelations } from 'containers/Relations/actions';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { LayoutMap } from 'models';
import { handleSagaError } from 'services/handleError';
import { createLayoutAndRelations } from 'utils/createLayoutAndRelations';
import { setLayout } from '../actions';
import * as Models from '../models';
import { orderedActiveLayoutIds, orderedActiveLayoutsBySectionId } from '../selectors';
import { CAROUSEL_ITEM_TYPES, CarouselItem, createCarouselItem } from './addCarousel';

function createCarouselSlideItem(carouselId: string, slideNumber: number): CarouselItem {
  return createCarouselItem(CAROUSEL_ITEM_TYPES.SLIDE, carouselId, uuid(), slideNumber);
}

export function* addCarouselSlide(action: Models.Action.IAddCarouselSlide): Generator {
  try {
    const { carouselId, sectionId, slideNumber } = action.payload;
    const layoutPosition: ReturnTypeSaga<typeof dragHotspotPosition> = yield select(dragHotspotPosition);
    if (layoutPosition === null) {
      return;
    }
    const artboard: ReturnTypeSaga<typeof activeArtboard> = yield select(activeArtboard);
    const layoutsBySectionId: ReturnTypeSaga<typeof orderedActiveLayoutsBySectionId> = yield select(orderedActiveLayoutsBySectionId);
    const type = LayoutType.TEXT_ONE_COLUMN;
    const sectionLayouts = layoutsBySectionId.get(sectionId);
    const carouselSlides = sectionLayouts?.filter((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');

      return carouselItem?.get('carouselId') === carouselId && carouselItem?.get('type') === CAROUSEL_ITEM_TYPES.SLIDE;
    });

    if (!carouselSlides) {
      return;
    }

    const slidesToUpdate = carouselSlides.filter((layout) => {
      const currentSlideNumber = (layout as LayoutMap).get('carouselItem')?.get('slideNumber') ?? 0;

      return currentSlideNumber >= slideNumber;
    });

    const newCarouselSlideItem = createCarouselSlideItem(carouselId, slideNumber);

    const { layout, relations } = createLayoutAndRelations(
      { type, section: sectionId },
      { carouselItem: newCarouselSlideItem },
    );

    const incrementedSlidesToUpdate = slidesToUpdate.map((slideLayout) => {
      const currentCarouselItem = (slideLayout as LayoutMap).get('carouselItem');
      const currentSlideNumber = currentCarouselItem?.get('slideNumber') ?? 0;
      const updatedCarouselItem = currentCarouselItem?.set('slideNumber', currentSlideNumber + 1);

      return (slideLayout as LayoutMap).set('carouselItem', updatedCarouselItem);
    });
    const layoutIds: ReturnType<typeof orderedActiveLayoutIds> = yield select(orderedActiveLayoutIds);
    const updatedArtboard = artboard.set('layoutIds', layoutIds.insert(layoutPosition, layout.id));

    yield put(batchActions([
      saveAppState(),
      addRelations(relations),
      setLayout(layout),
      ...incrementedSlidesToUpdate.map(slideLayout => setLayout(slideLayout)),
      updateArtboard(updatedArtboard),
    ]));
  } catch (error) {
    yield call(handleSagaError, error, 'Layouts.addCarouselSlide', 'AddCarouselSlide');
  }
}
